import React, { useLayoutEffect } from "react";
import * as d3 from "d3";
import cn from "classnames";
import BreachAnalytics from "../../../../components/BreachAnalitycs";
import { Collapse, Table } from "antd";
import { PieChart } from "../../../../components/Charts/PieChart";
import { ExportImgIco, TableIco } from "../../../../assets/customIcons";
import { observer } from "mobx-react-lite";
import { runInAction, toJS } from "mobx";
import RegistryApi from "../../../../api/Registry";

import "./index.scss";
import defectRegister from "../../../../store/defectRegister";
import { StoreAnalytics } from "../../../../store/storeAnalytics";
import Constants from "../../../../constants/Common";
import { Organizations } from "../../../../store/organizations";
import { Spinner } from "../../../../ui-kit";

const { Panel } = Collapse;

interface IAnalyticsProps {
  store: StoreAnalytics;
}

const organizationMap = {
  GUPTEK: 3528, // ГУП «ТЭК
  TEPLOSET: 3520, // Теплосеть
  PETERBURGTEPLOENERGO: 3554, // Петербургтеплоэнерго
  VODOKANAL: 3526, // Водоканал
  PETERBURGGAS: 3553, // ПетербургГаз
  LENSVENT: 989, // Ленсвет
};
const HEAT_SUPPLY = "HeatSupply";
const WATER_SUPPLY = "WaterSupply";
const GAS_SUPPLY = "GasSupply";
const OUTDOOR_LIGHTING = "OutdoorLighting";
const WATER_DRAINAGE = "WaterDrainage";
const POWER_SUPPLY = "PowerSupply";

const colorMap = {
  [HEAT_SUPPLY]: "#FFCA50",
  [WATER_SUPPLY]: "#A6E8F0",
  [GAS_SUPPLY]: "#92CBF9",
  [OUTDOOR_LIGHTING]: "#E5F894",
  [WATER_DRAINAGE]: "#9013FE",
  [POWER_SUPPLY]: "#A4E063",
};
const semiColorMapTwo = {
  [HEAT_SUPPLY]: "#FFB405",
  [WATER_SUPPLY]: "#80DEEA",
  [GAS_SUPPLY]: "#64B5F6",
  [OUTDOOR_LIGHTING]: "#DAF567",
  [WATER_DRAINAGE]: "#9013FE",
  [POWER_SUPPLY]: "#7ED321",
};
const getColor = (type) => {
  switch (type) {
    case 1:
      return colorMap[HEAT_SUPPLY];
    case 2:
      return colorMap[WATER_SUPPLY];
    case 3:
      return colorMap[GAS_SUPPLY];
    case 4:
      return colorMap[OUTDOOR_LIGHTING];
    case 5:
      return colorMap[WATER_DRAINAGE];
    case 6:
      return colorMap[POWER_SUPPLY];
    default:
      return "#000";
  }
};
const semiColorMap = {
  [HEAT_SUPPLY]: "#FFCA50",
  [WATER_SUPPLY]: "#A6E8F0",
  [GAS_SUPPLY]: "#92CBF9",
  [OUTDOOR_LIGHTING]: "#E5F894",
  [WATER_DRAINAGE]: "#9013FE",
  [POWER_SUPPLY]: "#A4E063",
};
const chartColors = {
  1: "#FF7070",
  2: "#ffe141",
  3: "#A4E063",
};
const chartColorRanges = {
  1: ["#FF7D7D", "#A43030"],
  2: ["#b69d30", "#ffe141"],
  3: ["#C8FF8D", "#63B608"],
};

const orgStore = new Organizations();

export const Analytics: React.FC<IAnalyticsProps> = observer(({ store }) => {
  const {
    titleIcons: { print, view },
  } = Constants;
  const barChartRef = React.useRef(null);
  useLayoutEffect(() => {
    orgStore.getOrganizations("isRso");
  }, []);
  const exportCustom = () => {
    const printContents = document.getElementById("bar-chart").innerHTML;
    const myWindow = window.open("", "", "width=1024px,height=768px");
    myWindow.document.write(printContents);
    myWindow.print();

    // document.body.innerHTML = originalContents;
  };
  // const exportBarChart = () => {
  //   d3ToPng(".bar-chart", "barChart", {
  //     scale: 3,
  //     format: "png",
  //     quality: 0.95,
  //     download: true,
  //   });
  // };

  const drawBarChartInternal = (data) => {
    const chart = window.document.querySelector(".bar-chart");
    if (!chart) return;
    chart.innerHTML = "";
    const svg = d3.select(".bar-chart"),
      margin = { top: -30, right: 0, bottom: 50, left: 150 },
      width = +svg.attr("width") - margin.left - margin.right,
      height = +svg.attr("height") - margin.top - margin.bottom * 2;

    const x = d3.scaleLinear().range([0, width]);
    const y = d3.scaleBand().range([height, 0]);

    const g = svg
      .append("g")
      .attr(
        "transform",
        "translate(" + (margin.left - 10) + "," + margin.right + ")",
      );
    data.sort((a, b) => {
      return a.value - b.value;
    });

    x.domain([
      0,
      d3.max(data, (d) => {
        return d.value;
      }),
    ]);
    y.domain(
      data.map((d) => {
        return d.label;
      }),
    ).padding(0.4);

    g.append("g")
      .attr("class", "x axis")
      .attr("transform", "translate(0," + height + ")")
      .call(
        d3
          .axisBottom(x)
          .ticks(12)
          .tickFormat((d) => {
            return d;
          })
          .tickSizeInner([-height]),
      );

    g.append("g").attr("class", "y axis").call(d3.axisLeft(y));

    g.selectAll()
      .data(data)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("fill", (d) => d.color)
      .attr("x", 0)
      .attr("height", y.bandwidth())
      .attr("y", (d) => {
        return y(d.label);
      })
      .attr("width", (d) => {
        return x(d.value);
      });
    g.selectAll()
      .data(data)
      .enter()
      .append("text")
      .text((d) => d.value)
      .attr("x", 10)
      .attr("y", (d) => {
        return y(d.label) + y.bandwidth() / 1.5;
      })
      .attr("fill", "black")
      .attr("class", "indicators-text");
  };

  const drawBarChart = () => {
    const data = store.defectsByGridTypes.map((x) => {
      return {
        label: x.title,
        value: x.count,
        color: colorMap[x.code],
        semiColor: semiColorMap[x.code],
      };
    });

    drawBarChartInternal(data);
  };
  React.useEffect(() => {
    store.getDefectsByGridTypes();
    store.getEventsByOrganization();
    store.getEventsByStatus();
    store.getSummary();
  }, [
    store.DateTo,
    store.DateFrom,
    store.organizationIds,
    store.gridTypeIds,
    store.Eliminated,
    store.OverBuildingsFrom,
    store.OverBuildingsTo,
    store.HospitalBuildingsFrom,
    store.HospitalBuildingsTo,
    store.ChildrenBuildingsFrom,
    store.ChildrenBuildingsTo,
    store.LivingBuildingsFrom,
    store.LivingBuildingsTo,
    store.DisconnectedFrom,
    store.DisconnectedTo,
  ]);

  const orgs = toJS(store.orgs);
  orgs.forEach((org) => {
    org.data.forEach((d) => {
      d.color = chartColors[d.eventTypeId];
    });
  });
  const list = toJS(store.list);
  list.forEach((evt) => {
    const range = chartColorRanges[evt.eventTypeId];
    evt.data.forEach((d, index) => {
      const org = orgStore.organizations.find(
        (org) => org?.name === evt.data[index]?.orgName,
      );
      const color = getColor(org?.gridTypeIds?.[0] || 0);
      const interpolate = d3.interpolateRgb(color, color);
      d.color = interpolate((index / evt.data.length) * 4);
    });
  });
  list[0]?.data?.sort((a, b) => b.count - a.count);
  list[1]?.data?.sort((a, b) => b.count - a.count);
  drawBarChart();

  const barChartActions = (
    <>
      <ExportImgIco title={print} onClick={exportCustom} />
      <TableIco
        title={view}
        onClick={() =>
          runInAction(() => (store.tableModeAnalyz = !store.tableModeAnalyz))
        }
      />
    </>
  );
  const exportCustomTwo = () => {
    const printContents = document.getElementById("chart-wrappers").innerHTML,
      printContentsTitle =
        document.getElementById("statusChartTitle").innerHTML;
    const myWindow = window.open("", "", "width=1024px,height=768px");
    myWindow.document.write(printContents);
    myWindow.print();
  };
  const сhartStatusImg = (
    <>
      <ExportImgIco title={print} onClick={exportCustomTwo} />
      <TableIco
        title={view}
        onClick={() =>
          runInAction(
            () => (store.tableModeEvetnStatus = !store.tableModeEvetnStatus),
          )
        }
      />
    </>
  );
  const exportCustomThree = () => {
    const printContents = document.getElementById("orgChart").innerHTML,
      printContentsTitle = document.getElementById("orgChartTitle").innerHTML;
    const myWindow = window.open("", "", "width=1024px,height=768px");
    myWindow.document.write(printContents);
    myWindow.print();
    // document.body.innerHTML = originalContents;
  };
  const orgChart = (
    <>
      <ExportImgIco title={print} onClick={exportCustomThree} />
      <TableIco
        title={view}
        onClick={() =>
          runInAction(
            () => (store.tableModeEventOrg = !store.tableModeEventOrg),
          )
        }
      />
    </>
  );

  const analiticsClassName = cn({
    analytics__body: true,
    "open-panel_left": !defectRegister.siderCollapsed,
  });
  const columnsOrgChart = [
    {
      title: "Организация",
      dataIndex: "org",
      key: "org",
      width: "auto",
      render: (org) => (
        <>
          <span
            style={{ textAlign: "left", display: "block", padding: "0 12px" }}
          >
            {org}
          </span>
        </>
      ),
    },
    {
      title: "Аварии",
      dataIndex: "crash",
      key: "crash",
      width: "95px",
      render: (crash) => (
        <>
          {crash.map((crash, idx) => {
            return (
              <span
                style={{
                  textAlign: "center",
                  display: "block",
                }}
                key={idx}
              >
                {crash}
              </span>
            );
          })}
        </>
      ),
    },
    {
      title: "Инциденты",
      dataIndex: "incident",
      key: "incident",
      width: "95px",
      render: (incident) => (
        <>
          <span
            style={{ textAlign: "center", display: "block", padding: "0 12px" }}
          >
            {incident}
          </span>
        </>
      ),
    },
  ];
  const data = list[1]?.data.map((item, idx) => {
    return {
      key: `${idx}`,
      org: item?.orgName,
      crash: list[0]?.data.map((crash) =>
        crash?.orgName === item?.orgName ? crash?.count : "",
      ),
      incident: item?.count,
    };
  });
  const columnsOrgEvent = [
    {
      title: "Организация",
      dataIndex: "org",
      key: "org",
      width: "auto",
      render: (org) => (
        <>
          <span
            style={{ textAlign: "left", display: "block", padding: "0 12px" }}
          >
            {org}
          </span>
        </>
      ),
    },
    {
      title: "Аварии",
      dataIndex: "crash",
      key: "crash",
      width: "auto",
      render: (crash) => (
        <>
          <span style={{ textAlign: "center", display: "block" }}>{crash}</span>
        </>
      ),
    },
    {
      title: "Инциденты",
      dataIndex: "incident",
      key: "incident",
      width: "auto",
      render: (incident) => (
        <>
          <span
            style={{ textAlign: "center", display: "block", padding: "0 12px" }}
          >
            {incident}
          </span>
        </>
      ),
    },
    {
      title: "Плановые отключения",
      dataIndex: "plannedFeelings",
      key: "plannedFeelings",
      width: "auto",
      render: (plannedFeelings) => (
        <>
          <span
            style={{ textAlign: "left", display: "block", padding: "0 12px" }}
          >
            {plannedFeelings}
          </span>
        </>
      ),
    },
  ];
  const dataOrgEvent = orgs?.map((item, idx) => {
    return {
      key: `${idx}`,
      org: item?.orgName,
      crash: item?.data[0]?.count,
      incident: item?.data[1]?.count,
      plannedFeelings: item?.data[2]?.count,
    };
  });
  const columnsBar = [
    {
      title: "Тип сети",
      dataIndex: "gridType",
      key: "gridType",
      width: "auto",
      render: (gridType) => (
        <span
          style={{ textAlign: "left", display: "block", padding: "0 12px" }}
        >
          {gridType}
        </span>
      ),
    },
    {
      title: "Количество",
      dataIndex: "count",
      key: "count",
      width: "auto",
      render: (count) => (
        <span
          style={{ textAlign: "left", display: "block", padding: "0 12px" }}
        >
          {count}
        </span>
      ),
    },
  ];
  const dataBar = store.defectsByGridTypes?.map((item, idx) => {
    return {
      key: `${idx}`,
      gridType: item?.title,
      count: item?.count,
    };
  });
  return (
    <div className={analiticsClassName}>
      <BreachAnalytics {...store.summaryData} mode="horz" />
      <div className="analytics__charts">
        <div className="analytics__chart" id="bar-chart">
          <Panel
            key="1"
            header="Анализ нарушений по типу сети"
            className="chart-title"
            extra={barChartActions}
          />
          <div className="main-bar-container">
            <Table
              style={{
                maxWidth: "1000px",
                width: "100%",
                display: store.tableModeAnalyz ? "block" : "none",
              }}
              pagination={false}
              columns={columnsBar}
              dataSource={dataBar}
            />

            <svg
              style={{
                display: !store.tableModeAnalyz ? "block" : "none",
              }}
              ref={barChartRef}
              className="bar-chart"
              width="750"
              height="460"
              viewBox="0 0 750 460"
            />
          </div>
        </div>
        <div className="analytics__chart">
          <Panel
            key="2"
            header="Диаграммы типов событий"
            className="chart-title"
          />

          <Panel
            key="4"
            header="Тип событий по организациям"
            className="chart-subtitle orgChartPrint"
            extra={orgChart}
            id="orgChartTitle"
          />

          {orgStore.isLoadingOrg.value ? (
            <div id="Spinner" className="panel-container">
              <Spinner />
            </div>
          ) : (
            <div
              id="orgChart"
              className="panel-container orgChart orgChartPrint"
            >
              {store.tableModeEventOrg ? (
                <Table
                  style={{ maxWidth: "1000px", width: "100%" }}
                  pagination={false}
                  columns={columnsOrgChart}
                  dataSource={data}
                />
              ) : (
                list?.map((x) => {
                  return (
                    <>
                      <div className="chart-wrapper-outer">
                        <div className="chart-wrapper">
                          <PieChart title={x.eventType} data={x.data} />
                        </div>
                        <ul className="chart-legend-vert">
                          {x.data.length &&
                            x.data.map((d, idx) => {
                              return (
                                <li key={idx}>
                                  <div
                                    className="legend-square"
                                    style={{ border: "6px solid " + d.color }}
                                  />
                                  <div>{d.orgName}</div>
                                </li>
                              );
                            })}
                        </ul>
                      </div>
                    </>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
      <div className="analytics__chart analytics__chart_type_bottom">
        <div className="chart-containerLegend">
          <Panel
            key="3"
            header="Тип событий по статусу"
            className="chart-title"
            extra={сhartStatusImg}
            id="statusChartTitle"
          />
          <ul className="chart-legend">
            <li>
              <i className="item-eventtype-3" />
              <span>Плановые отключения</span>
            </li>
            <li>
              <i className="item-eventtype-2" />
              <span>Инциденты</span>
            </li>
            <li>
              <i className="item-eventtype-1" />
              <span>Аварии</span>
            </li>
          </ul>
        </div>
        <div id="statusChart" className="panel-container first statusChart">
          {store.tableModeEvetnStatus ? (
            <Table
              style={{ maxWidth: "1000px", width: "100%" }}
              pagination={false}
              columns={columnsOrgEvent}
              dataSource={dataOrgEvent}
            />
          ) : (
            <div>
              <div id={"chart-wrappers"} className="chart-wrappers">
                {orgs &&
                  orgs.map((x, idx) => {
                    return (
                      <>
                        <div key={idx} className="chart-wrapper">
                          <PieChart title={x.orgName} data={x.data} />
                        </div>
                      </>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
